import { Clapperboard, MessageSquare, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import type { NovelImportScreenplayPreview } from '@/types/nf'

export function ScreenplayImportPreviewPanel({ screenplay }: { screenplay: NovelImportScreenplayPreview }) {
  const { t } = useTranslation()
  const formatLabel = screenplay.format === 'fountain'
    ? t('reader.exportFormats.fountain')
    : t('reader.exportFormats.fdx')

  return (
    <div className="space-y-4 rounded-lg border bg-muted/15 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Clapperboard className="size-4 text-primary" />
            {t('novelImport.screenplay.title')}
            <Badge variant="outline">{formatLabel}</Badge>
          </p>
          <p className="mt-1 text-sm text-muted-foreground">{t('novelImport.screenplay.description', { format: formatLabel })}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge variant="secondary">{t('novelImport.screenplay.sceneCount', { count: screenplay.scene_count })}</Badge>
          <Badge variant="outline">{t('novelImport.screenplay.dialogueCount', { count: screenplay.dialogue_count })}</Badge>
        </div>
      </div>

      {screenplay.characters.length ? (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Users className="size-4 text-muted-foreground" />
          <span className="text-muted-foreground">{t('novelImport.screenplay.characters')}</span>
          {screenplay.characters.map((character) => (
            <Badge key={character} variant="outline">{character}</Badge>
          ))}
        </div>
      ) : null}

      <div className="space-y-3">
        {screenplay.chapters.map((chapter) => (
          <details key={`${chapter.index}-${chapter.title}`} className="rounded-lg border bg-background/70 p-4" open={chapter.index <= 2}>
            <summary className="cursor-pointer list-none">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">
                    {t('novelImport.chapterLine', { index: chapter.index, title: chapter.title })}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t('novelImport.screenplay.chapterStats', {
                      scenes: chapter.scene_count,
                      dialogues: chapter.dialogue_count,
                    })}
                  </p>
                </div>
                {chapter.characters.length ? (
                  <span className="text-xs text-muted-foreground">
                    {chapter.characters.slice(0, 6).join(', ')}
                  </span>
                ) : null}
              </div>
            </summary>

            <div className="mt-4 space-y-3">
              {chapter.scene_samples.map((scene) => (
                <div key={`${chapter.index}-${scene.scene_id}`} className="rounded-md border bg-card/70 p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="secondary">{scene.scene_id || t('novelImport.screenplay.scene')}</Badge>
                    <p className="text-sm font-medium">{scene.setting || t('novelImport.screenplay.unknownSetting')}</p>
                  </div>
                  {scene.action ? (
                    <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-muted-foreground">{scene.action}</p>
                  ) : null}
                  {scene.dialogues.length ? (
                    <div className="mt-3 space-y-2">
                      {scene.dialogues.map((dialogue, index) => (
                        <p key={`${dialogue.character}-${index}`} className="flex gap-2 text-sm">
                          <MessageSquare className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                          <span>
                            <span className="font-medium text-foreground">{dialogue.character}</span>
                            <span className="text-muted-foreground">: {dialogue.content}</span>
                          </span>
                        </p>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </details>
        ))}
      </div>
    </div>
  )
}
